import { useMemo } from 'react';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';

import { InlineReportChart } from '@/components/reasoning/InlineReportChart';
import { StepLimitWarningBanner } from '@/components/reasoning/StepLimitWarningBanner';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { preprocessEntityTags } from '@/lib/markdown/entityRendering';
import { ChainResultsPreview } from '@/lib/proposals/ChainResultsPreview';
import type { FeedbackRating } from '@/lib/reasoning/feedback';
import type { ViewDefinition } from '@/types/dynamic_views';

import {
  EntityMentionSpan,
  MarkdownCopyableTable,
} from '../reasoningMarkdownComponents';
import { sanitizeSchema } from '../reasoningSanitizeSchema';
import { ChainActionBar } from './ChainActionBar';
import { ChainProposals } from './ChainProposals';
import {
  collectChainDynamicViews,
  collectChainProposals,
  findLatestReportManifest,
} from './chainResponseState';
import type { ChatChain, ChatStep } from './types';

// Module-scope so MarkdownContent receives the same references every render.
// EntityMentionSpan reads orgId via useParams(), so no closure is needed.
const MARKDOWN_COMPONENTS = {
  span: EntityMentionSpan,
  table: MarkdownCopyableTable,
};
const REMARK_PLUGINS = [remarkBreaks];
const REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, sanitizeSchema]];
const NO_PROPOSALS: never[] = [];
const NO_STEPS: ChatStep[] = [];

export interface ChainFinalAnswerProps {
  chain: ChatChain;
  finalStep: ChatStep;
  orgId: number;
  isLastChain: boolean;
  /** A run is in flight on this conversation. */
  hasActiveTask: boolean;
  regeneratingChainId: string | null;
  feedback: FeedbackRating | null | undefined;
  showInlineProposals: boolean;
  isBuilderConversation: boolean;
  shownProposalId: number | null | undefined;
  onSelectProposal: (proposalId: number) => void;
  onOpenDynamicViewSidebar: (viewId: number) => void;
  onOpenReasoningSidebar: (chainId: string) => void;
  onRetry: (chainId: string) => void;
  onFeedback: (chainId: string, rating: FeedbackRating) => void;
}

/** A chain's final answer: report chart, prose, results, and the action row. */
export function ChainFinalAnswer({
  chain,
  finalStep,
  orgId,
  isLastChain,
  hasActiveTask,
  regeneratingChainId,
  feedback,
  showInlineProposals,
  isBuilderConversation,
  shownProposalId,
  onSelectProposal,
  onOpenDynamicViewSidebar,
  onOpenReasoningSidebar,
  onRetry,
  onFeedback,
}: ChainFinalAnswerProps) {
  const steps = chain.steps ?? NO_STEPS;
  const reportManifest = useMemo(
    () => findLatestReportManifest(steps),
    [steps]
  );
  // Only mount the chart when the manifest actually carries a component array.
  // A malformed manifest with non-array `components` makes InlineReportChart's
  // `.find` throw during render, which would blank the whole final answer.
  const hasRenderableReport =
    reportManifest !== null && Array.isArray(reportManifest.components);
  const proposals = useMemo(() => collectChainProposals(steps), [steps]);
  const dynamicViews = useMemo(() => collectChainDynamicViews(steps), [steps]);
  const content = useMemo(
    () => preprocessEntityTags(finalStep.content),
    [finalStep.content]
  );

  return (
    <div className='space-y-3' data-component='chat-final-answer'>
      {/* Above the prose, so users see the visualisation first without
          opening the Reasoning side panel. */}
      {hasRenderableReport && (
        <InlineReportChart
          manifest={reportManifest as unknown as ViewDefinition}
        />
      )}
      <div className='text-base font-normal text-fg-primary leading-7'>
        <MarkdownContent
          content={content}
          remarkPlugins={REMARK_PLUGINS}
          rehypePlugins={REHYPE_PLUGINS}
          components={MARKDOWN_COMPONENTS}
        />
      </div>

      {chain.step_limit_reached && <StepLimitWarningBanner className='mt-3' />}

      <ChainProposals
        proposals={proposals}
        orgId={orgId}
        showInline={showInlineProposals}
        isBuilderConversation={isBuilderConversation}
        isFresh={isLastChain && hasActiveTask}
        shownProposalId={shownProposalId}
        onSelectProposal={onSelectProposal}
      />

      {dynamicViews.length > 0 && (
        <ChainResultsPreview
          proposals={NO_PROPOSALS}
          dynamicViews={dynamicViews}
          onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
        />
      )}

      <ChainActionBar
        chainId={chain.id}
        content={finalStep.content}
        hasSteps={steps.length > 0}
        canRetry={isLastChain && chain.status === 'completed'}
        isRetryDisabled={Boolean(regeneratingChainId)}
        isRegenerating={regeneratingChainId === chain.id}
        feedback={feedback}
        onRetry={onRetry}
        onFeedback={onFeedback}
        onOpenReasoningSidebar={onOpenReasoningSidebar}
      />
    </div>
  );
}
